import { useDispatch } from "react-redux";
import styled from "styled-components";
import {
  BASIC_DARK_GRAY,
  BASIC_LIGHT_BLUE,
  BASIC_ORANGE,
  BASIC_WHITE,
} from "../../../constants/colors";
import { appActions } from "../../../ducks/app";
import { useAsset } from "../../../hooks/useAsset";
import { Badge } from "../../atoms/Badge";
import { Button } from "../../atoms/Button";
import { FavoriteIcon } from "../../atoms/FavoriteIcon";

type Props = {
  id?: number;
  name: string;
  address: string;
  phoneNumber: string;
  service: string;
  location: { lat: number; lng: number };
  isAdvancedPayment: boolean;
  isFavorite: boolean;
  onFavoriteClick: (favorite: boolean, onSuccess?: () => void) => void;
  showChangeButton?: boolean;
  onClickChangeButton?: () => void;
  pickupDateText?: string;
};

export const StoreDetail: React.FC<Props> = ({
  id,
  name,
  address,
  phoneNumber,
  service,
  location,
  isAdvancedPayment,
  isFavorite,
  onFavoriteClick,
  showChangeButton = false,
  onClickChangeButton,
  pickupDateText,
}) => {
  const dispatch = useDispatch();

  const { baseDir } = useAsset();

  return (
    <>
      <StoreCardTitle
        id={`store-title-${id}`}
        onClick={() =>
          dispatch(
            appActions.showStoreDetailPopup.started({
              name,
              address,
              phoneNumber,
              service,
              location,
              isFavorite,
              onFavorite: (favorite) => {
                onFavoriteClick(favorite, () => {
                  dispatch(appActions.setStoreDetailPopupFavorite(favorite));
                });
              },
              onClose: () => dispatch(appActions.closeStoreDetailPopup()),
            }),
          )
        }
      >
        <StoreInformationContainer>
          <Icon
            style={{
              backgroundImage: `url('${baseDir}/assets/images/icons/ico_shop01.png')`,
            }}
            $backgroundColor={BASIC_WHITE}
          ></Icon>
          <StoreInformation>
            <StoreName id={`store-name-${id}`}>{name}</StoreName>
            <StoreAddress id={`store-address-${id}`}>{address}</StoreAddress>

            {isAdvancedPayment && (
              <AdvancedPaymentContainer>
                <Badge
                  text="事前決済可能"
                  color={BASIC_LIGHT_BLUE}
                  rounded={false}
                  size="sm"
                />
              </AdvancedPaymentContainer>
            )}
          </StoreInformation>
        </StoreInformationContainer>

        <FavoriteIconContainer id={`store-favorite-${id}`}>
          <FavoriteIcon
            id={id}
            type="star"
            favorite={isFavorite}
            onClick={onFavoriteClick}
          />

          {showChangeButton && (
            <ChangeButtonContainer>
              <Button
                btnStyle="secondary"
                text="店舗変更"
                onClick={(e) => {
                  e.stopPropagation();
                  onClickChangeButton?.();
                }}
                size="xxs"
                rounded="xs"
              />
            </ChangeButtonContainer>
          )}
        </FavoriteIconContainer>
      </StoreCardTitle>

      {pickupDateText && (
        <PickUpDateTextContainer>
          <Text>
            お受け取り日時
            <PickupDateText>{pickupDateText}</PickupDateText>
          </Text>
          <DeliveryAttentionText>
            ※ご注文確定のお時間によっては、お受け取り日時がご選択いただいたお時間より変更となってしまう場合がございます。
          </DeliveryAttentionText>
        </PickUpDateTextContainer>
      )}
    </>
  );
};

const StoreCardTitle = styled.div`
  display: flex;
  flex-direction: row;
  padding: 0 0 15px 0;
  cursor: pointer;
`;

const Icon = styled.div<{ $backgroundColor: string }>`
  background-size: contain;
  background-position: center center;
  background-repeat: no-repeat;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  width: 38px;
  height: 38px;
  margin: 0 12px 0 0;
`;

const StoreInformationContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  align-items: start;
`;

const StoreInformation = styled.div`
  flex: 1;
  text-align: left;
  color: ${BASIC_DARK_GRAY};
`;

const FavoriteIconContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StoreName = styled.p`
  margin: 0;
  padding: 0;
  font-size: 20px;
  font-weight: 600;
`;

const StoreAddress = styled.p`
  margin: 0;
  padding: 0;
  font-weight: 600;
  font-size: 14px;
  flex: 1;
`;

const AdvancedPaymentContainer = styled.div`
  margin-top: 10px;
`;

const ChangeButtonContainer = styled.div`
  margin-left: 20px;
  button {
    padding: 9px 12px;
    margin-left: 32px
    letter-spacing: 0;
    width: auto;
    font-size: 12px;
    line-height: 1em;
    color: ${BASIC_DARK_GRAY};
    font-weight: 300;
  }
`;

const PickUpDateTextContainer = styled.div`
  text-align: left;
  margin-bottom: 15px;
`;

const DeliveryAttentionText = styled.p`
  text-align: left;
  margin: 0;
  padding-top: 10px;
  font-size: 14px;
  font-weight: 300;
  color: ${BASIC_ORANGE};
`;

const Text = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
`;

const PickupDateText = styled.span`
  font-size: 15px;
  font-weight: 500;
  margin-left: 10px;
`;
